if (!customElements.get('alexia-cart-discount')) {
  class AlexiaCartDiscount extends HTMLElement {
    static pending = null;

    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelector('form').hidden = false;
      this.querySelector('form').onsubmit = (event) => {
        event.preventDefault();
        this.change(this.input.value.trim());
      };
      this.querySelector('ul').onclick = (event) => {
        const button = event.target.closest('[data-remove-code]');
        if (button) this.change(button.dataset.removeCode, true);
      };
      const hydrate = () => { if (this.isConnected) this.hydrate(); };
      if (AlexiaCartDiscount.pending) AlexiaCartDiscount.pending.finally(hydrate);
      else hydrate();
    }

    disconnectedCallback() {
      this.readController?.abort();
    }

    async request(url, options = {}) {
      const response = await fetch(url, {
        cache: 'no-store', ...options, signal: options.signal || AbortSignal.timeout(15000)
      });
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }

    async hydrate() {
      this.readController?.abort();
      this.readController = new AbortController();
      try {
        const cart = await this.request(routes.cart_url + '.js', {
          signal: AbortSignal.any([this.readController.signal, AbortSignal.timeout(15000)])
        });
        if (this.isConnected) this.showCodes(cart);
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.message('error', true);
      }
    }

    codes(cart) {
      if (Array.isArray(cart.discount_codes)) return cart.discount_codes;
      return this.allocations(cart).map((entry) => ({ code: entry.title, applicable: true }));
    }

    allocations(cart) {
      return [
        ...(cart.cart_level_discount_applications || []).map((entry) => entry.discount_application || entry),
        ...(cart.items || []).flatMap((item) =>
          (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application))
      ].filter((entry) => entry.type === 'discount_code');
    }

    same(a, b) { return a.toUpperCase() === b.toUpperCase(); }

    showCodes(cart) {
      const list = this.querySelector('ul');
      list.replaceChildren();
      const seen = new Set();
      for (const entry of this.codes(cart)) {
        if (seen.has(entry.code.toUpperCase())) continue;
        seen.add(entry.code.toUpperCase());
        const li = document.createElement('li');
        const text = document.createElement('span');
        const allocated = this.allocations(cart).some((item) => this.same(item.title, entry.code));
        const status = entry.applicable === false ? 'unavailable' : allocated ? 'applied' : 'checkout';
        text.textContent = entry.code + ' — ' + this.dataset[status];
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'link';
        button.dataset.removeCode = entry.code;
        button.textContent = this.dataset.remove;
        button.setAttribute('aria-label', this.dataset.remove + ' ' + entry.code);
        li.append(text, button);
        list.append(li);
      }
    }

    message(key, fallback = false) {
      this.querySelector('[role="status"]').textContent = this.dataset[key];
      this.querySelector('.alexia-discount__fallback').hidden = !fallback;
    }

    change(code, remove = false) {
      if (AlexiaCartDiscount.pending) return;
      if (!code) { this.message('empty'); this.input.focus(); return; }
      if (code.includes(',')) { this.message('one'); return; }
      const operation = this.updateCart(code, remove);
      AlexiaCartDiscount.pending = operation;
      operation.finally(() => { AlexiaCartDiscount.pending = null; });
    }

    async updateCart(code, remove) {
      const drawer = this.closest('cart-drawer');
      const blocked = [...drawer.querySelectorAll('cart-drawer-items, .cart__ctas')];
      const inertStates = blocked.map((element) => element.inert);
      blocked.forEach((element) => { element.inert = true; });
      this.setAttribute('aria-busy', 'true');
      this.input.readOnly = true;
      this.querySelectorAll('button').forEach((button) => button.setAttribute('aria-disabled', 'true'));
      this.message('loading');
      try {
        await new Promise((resolve) => setTimeout(resolve, ON_CHANGE_DEBOUNCE_TIMER + 50));
        if (!this.isConnected || drawer.querySelector('.cart__items--disabled')) {
          drawer.querySelector('alexia-cart-discount')?.message('wait');
          return;
        }
        const current = await this.request(routes.cart_url + '.js');
        const codes = [...new Set(this.codes(current).map((entry) => entry.code))];
        const next = codes.filter((existing) => !this.same(existing, code));
        if (!remove) next.push(code);
        const targets = [
          { id: 'cart-drawer', selector: '.drawer__inner', target: drawer.querySelector('.drawer__inner') },
          { id: 'cart-icon-bubble', selector: '.shopify-section', target: document.getElementById('cart-icon-bubble') }
        ];
        for (const id of ['main-cart-items', 'main-cart-footer']) {
          const element = document.getElementById(id);
          if (element?.dataset.id) targets.push({ id: element.dataset.id, selector: '.js-contents', target: element.querySelector('.js-contents') });
        }
        const data = await this.request(routes.cart_update_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: next.join(','), sections: targets.map((target) => target.id), sections_url: location.pathname })
        });
        if (data.errors || data.status >= 400) throw new Error('Cart update rejected');
        let sections = data.sections;
        if (targets.some((target) => !sections?.[target.id])) {
          const url = new URL(location.href);
          url.searchParams.set('sections', targets.map((target) => target.id).join(','));
          sections = await this.request(url);
        }
        const replacements = targets.map((target) => ({
          ...target, source: new DOMParser().parseFromString(sections?.[target.id] || '', 'text/html').querySelector(target.selector)
        }));
        if (replacements.some((target) => target.target && !target.source)) throw new Error('Cart rendering failed');
        const restoreFocus = drawer.classList.contains('active') && drawer.contains(document.activeElement);
        replacements.forEach(({ target, source }) => { if (target && source) target.innerHTML = source.innerHTML; });
        drawer.classList.toggle('is-empty', data.item_count === 0);
        const component = drawer.querySelector('alexia-cart-discount');
        if (component) {
          component.showCodes(data);
          const result = this.codes(data).find((entry) => this.same(entry.code, code));
          const allocated = this.allocations(data).some((entry) => this.same(entry.title, code));
          component.message(remove ? (result ? 'error' : 'removed') :
            result?.applicable === false ? 'unavailable' : allocated ? 'applied' :
            result?.applicable === true ? 'checkout' : 'unavailable');
          if (restoreFocus) trapFocus(drawer.querySelector('#CartDrawer'), component.input);
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data });
      } catch (error) {
        drawer.querySelector('alexia-cart-discount')?.message('error', true);
      } finally {
        blocked.forEach((element, index) => { element.inert = inertStates[index]; });
        this.removeAttribute('aria-busy');
        this.input.readOnly = false;
        this.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-disabled'));
      }
    }
  }
  customElements.define('alexia-cart-discount', AlexiaCartDiscount);
}